// markdownItCustomLists.js
// Adds support for custom ordered list styles:
//   a) text, b) text    -> rendered as  a)  b)  c)
//   A) text, B) text    -> rendered as  A)  B)  C)
//   (i) text, (ii) text -> rendered as (i) (ii) (iii)
//   (I) text, (II) text -> rendered as (I) (II) (III)
//   (1) text, (2) text  -> rendered as (1) (2)  (3)
//
// Nested lists are supported by recursively tokenizing item content.

const ROMAN_RE = /^(m{0,4})(cm|cd|d?c{0,3})(xc|xl|l?x{0,3})(ix|iv|v?i{0,3})$/i;

function isRoman(str) {
  return str.length > 0 && ROMAN_RE.test(str);
}

// Returns { listType, markerLen } for the first non-space content of a line, or null.
function detectMarker(line) {
  // a) or A) — single letter followed by ") "
  if (line.length >= 3 && line[1] === ')' && line[2] === ' ') {
    const ch = line[0];
    if (ch >= 'a' && ch <= 'z') return { listType: 'lower-alpha', markerLen: 3 };
    if (ch >= 'A' && ch <= 'Z') return { listType: 'upper-alpha', markerLen: 3 };
  }
  // (roman) or (decimal) — content in parentheses followed by " "
  if (line[0] === '(') {
    const close = line.indexOf(')');
    if (close > 1 && close + 1 < line.length && line[close + 1] === ' ') {
      const inner = line.slice(1, close);
      const markerLen = close + 2; // includes trailing space
      if (/^\d+$/.test(inner)) return { listType: 'paren-decimal', markerLen };
      if (isRoman(inner)) {
        return {
          listType: inner === inner.toLowerCase() ? 'lower-roman' : 'upper-roman',
          markerLen,
        };
      }
    }
  }
  return null;
}

function getLineText(state, line) {
  return state.src.slice(state.bMarks[line] + state.tShift[line], state.eMarks[line]);
}

// Tokenize a list item's content as a full block (enabling nested lists, inline
// formatting, etc.).  Works by temporarily adjusting bMarks/tShift so that the
// block tokenizer sees the content with the list-marker indentation stripped.
function tokenizeItemContent(state, item, baseIndent) {
  const { startLine, endLine, markerLen } = item;

  // --- First line: skip past the marker ---
  const savedBMark0 = state.bMarks[startLine];
  const savedTShift0 = state.tShift[startLine];
  const savedSCount0 = state.sCount[startLine];

  const contentPos = savedBMark0 + savedTShift0 + markerLen;
  state.bMarks[startLine] = contentPos;
  state.tShift[startLine] = 0;
  state.sCount[startLine] = 0;

  // --- Continuation lines: strip the list indentation ---
  const stripLen = baseIndent + markerLen;
  const savedContLines = [];
  for (let i = startLine + 1; i < endLine; i++) {
    savedContLines.push({
      bMark: state.bMarks[i],
      tShift: state.tShift[i],
      sCount: state.sCount[i],
    });
    if (!state.isEmpty(i)) {
      const strip = Math.min(stripLen, state.tShift[i]);
      state.bMarks[i] += strip;
      state.tShift[i] -= strip;
      state.sCount[i] = Math.max(0, state.sCount[i] - stripLen);
    }
  }

  const oldParentType = state.parentType;
  const oldLineMax = state.lineMax;
  state.parentType = 'list';
  state.lineMax = endLine;

  // Recursive block tokenize — this will process nested lists, blockquotes, etc.
  state.md.block.tokenize(state, startLine, endLine);

  // --- Restore all saved values ---
  state.bMarks[startLine] = savedBMark0;
  state.tShift[startLine] = savedTShift0;
  state.sCount[startLine] = savedSCount0;
  for (let i = startLine + 1; i < endLine; i++) {
    const s = savedContLines[i - startLine - 1];
    state.bMarks[i] = s.bMark;
    state.tShift[i] = s.tShift;
    state.sCount[i] = s.sCount;
  }

  state.parentType = oldParentType;
  state.lineMax = oldLineMax;
}

function customListRule(state, startLine, endLine, silent) {
  const lineText = getLineText(state, startLine);
  const firstMarker = detectMarker(lineText);
  if (!firstMarker) return false;
  if (silent) return true;

  const { listType } = firstMarker;
  const baseIndent = state.sCount[startLine];

  // --- Collect item boundaries ---
  // Items are lines at baseIndent matching the same listType.
  // Continuation lines have a strictly greater indentation.
  // A blank line or a line at a lower/same indent that doesn't match ends the list.
  const items = [];
  let currentLine = startLine;

  while (currentLine < endLine) {
    if (state.isEmpty(currentLine)) break;

    const indent = state.sCount[currentLine];
    const text = getLineText(state, currentLine);

    if (indent === baseIndent) {
      const marker = detectMarker(text);
      if (marker && marker.listType === listType) {
        // Close previous item's endLine before opening new one
        if (items.length > 0) {
          items[items.length - 1].endLine = currentLine;
        }
        items.push({
          startLine: currentLine,
          markerLen: marker.markerLen,
          endLine: currentLine + 1,
        });
        currentLine += 1;
      } else {
        break;
      }
    } else if (indent > baseIndent && items.length > 0) {
      // Continuation or nested content of the current item
      items[items.length - 1].endLine = currentLine + 1;
      currentLine += 1;
    } else {
      break;
    }
  }

  if (!items.length) return false;
  state.line = currentLine;

  // --- Emit tokens ---
  let token = state.push('ordered_list_open', 'ol', 1);
  token.attrSet('class', `list-${listType}`);
  token.map = [startLine, currentLine];

  for (const item of items) {
    token = state.push('list_item_open', 'li', 1);
    token.map = [item.startLine, item.endLine];

    if (item.endLine > item.startLine + 1) {
      // Multi-line item: recursive block tokenize handles nested lists, inline
      // markup, paragraphs, etc.
      tokenizeItemContent(state, item, baseIndent);
    } else {
      // Single-line item: emit an inline token directly (no <p> wrapping).
      token = state.push('inline', '', 0);
      token.content = getLineText(state, item.startLine).slice(item.markerLen);
      token.children = [];
      token.map = [item.startLine, item.endLine];
    }

    state.push('list_item_close', 'li', -1);
  }

  state.push('ordered_list_close', 'ol', -1);
  return true;
}

export default (md) => {
  md.block.ruler.before('list', 'custom_list', customListRule, {
    alt: ['paragraph', 'reference', 'blockquote'],
  });
};



// WEBPACK FOOTER //
// ./src/extensions/libs/markdownItCustomLists.js